import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { BarChart3, Search, Plus, Edit, Trash2, RefreshCcw, CheckCircle, AlertTriangle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileText, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Layers, Shield, Activity, Clock, List, LayoutList } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
} from '../../products/saudi/components/QawaemTableSelection';
import { useComptabiliteTableColumnVisibility, useComptabiliteListRowSelection, ComptabiliteBulkDeleteFilterAction, COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS } from './utils/comptabiliteListPageUtils';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  getRapportsPeriodiques,
  createRapportPeriodique,
  updateRapportPeriodique,
  deleteRapportPeriodique } from '../../shared/utils/comptabiliteService';
import { fetchCurrentFiscalYearContext, resolveFiscalYearLabel } from './utils/fiscalYearUtils';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import { formatComptabiliteDate, getComptabiliteDateLocale, getExportCurrencyHeader } from './utils/comptabiliteSaudiFormat';
import { getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import arSA from 'date-fns/locale/ar-SA';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import '../../core/styles/global-dropdowns.css';
import ComptabiliteFiscalYearField from './components/ComptabiliteFiscalYearField';

registerLocale('ar', arSA);

const RAPPORTS_LIST_COLUMNS = [
  { key: 'rapport_ref', labelKey: 'col_reference' },
  { key: 'titre_rapport', labelKey: 'col_report_title' },
  { key: 'type_rapport', labelKey: 'col_type' },
  { key: 'categorie_rapport', labelKey: 'col_category' },
  { key: 'date_generation', labelKey: 'col_generation_date' },
  { key: 'statut_rapport', labelKey: 'col_status' },
];

const RapportsperiodiquesPage = () => {
  const { t, i18n } = useTranslation();
  const { isDashboardLightMode } = useGlobalTheme();
  const p = (key, opts) => t(`modules.comptabilite.pages.rapportsperiodiquesPage.${key}`, opts);
  const dateLocale = getComptabiliteDateLocale(i18n.language);
  const fmtDate = (value) => formatComptabiliteDate(value, i18n.language);
  const fmtMonthYear = (date) => formatComptabiliteDate(date, i18n.language, { monthYear: true });

  const translateStatut = (value) => {
    const map = {
      'En cours': p('status_in_progress'),
      'Finalisé': p('status_finalized'),
      'Validé': p('status_validated'),
      'Publié': p('status_published'),
      'Archivé': p('status_archived') };
    return map[value] || value;
  };

  const translateType = (value) => {
    const map = {
      'Mensuel': p('type_monthly'),
      'Trimestriel': p('type_quarterly'),
      'Semestriel': p('type_semiannual'),
      'Annuel': p('type_annual'),
      'Exceptionnel': p('type_exceptional') };
    return map[value] || value;
  };

  const translateCategory = (value) => {
    const map = {
      'Financier': p('cat_financial'),
      'Comptable': p('cat_accounting'),
      'Gestion': p('cat_management'),
      'Fiscal': p('cat_fiscal'),
      'Réglementaire': p('cat_regulatory'),
      'Interne': p('cat_internal') };
    return map[value] || value;
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [rapports, setRapports] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  useEffect(() => {
    if (viewMode === 'table' && String(searchTerm || '').trim()) {
      setSearchTerm('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  const [rapportsperiodiquesFormTab, setRapportsperiodiquesFormTab] = useState('rapportInfo');
  const rapportsperiodiquesFormTabs = useMemo(() => ([
    { id: 'rapportInfo', label: p('section_rapport_info'), icon: ChevronUp },
    { id: 'validationConformite', label: p('section_validation_diffusion'), icon: ChevronUp },
  ]), [p]);

  
  const [formData, setFormData] = useState({ 
    rapport_ref: '',
    titre_rapport: '',
    description_rapport: '',
    type_rapport: '',
    categorie_rapport: '',
    exercice_fiscal: '',
    periode_debut: '',
    periode_fin: '',
    statut_rapport: '',
    niveau_confidentialite: '',
    format_rapport: '',
    validation_daf: '',
    archivage_obligatoire: '',
    conformite_reglementaire: '',
    notes: ''
  });
  const [fiscalYearLabel, setFiscalYearLabel] = useState('');
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [setDateRange] = useState({ start: null, end: null });
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_rapports');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);

  const { filters: columnFilters, setFilter: setColumnFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const columnFilterRules = useMemo(() => [
    textColumnRule('rapport_ref', (row) => row.rapport_ref ?? ''),
    textColumnRule('titre_rapport', (row) => row.titre_rapport ?? ''),
    textColumnRule('type_rapport', (row) => row.type_rapport ?? ''),
    textColumnRule('date_generation', (row) => row.date_generation ?? ''),
    textColumnRule('statut_rapport', (row) => row.statut_rapport ?? ''),
  ], []);

  useEffect(() => {
    loadRapports();
    fetchCurrentFiscalYearContext()
      .then(({ label }) => { if (label) setFiscalYearLabel(label); })
      .catch(() => {});
  }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadRapports = async () => {
    try {
      const response = await getRapportsPeriodiques();
      setRapports(sortRecordsNewestFirst(Array.isArray(response) ? response : []));
    } catch (error) {
      console.error('Error loading rapports:', error);
      setRapports([]);
    }
  };

  const stats = {
    total: rapports.length,
    enCours: rapports.filter(r => r.statut_rapport === 'En cours').length,
    publies: rapports.filter(r => r.statut_rapport === 'Publié').length,
    mensuels: rapports.filter(r => r.type_rapport === 'Mensuel').length,
    validationsAttente: rapports.filter(r => !r.validation_daf || !r.validation_dg).length
  };

  const mainTabs = [
    { id: 'all_rapports', nameKey: 'tab_overview', icon: LayoutGrid },
    { id: 'par_type', nameKey: 'tab_by_type', icon: BarChart3 },
    { id: 'par_statut', nameKey: 'tab_by_status', icon: Shield },
    { id: 'par_categorie', nameKey: 'tab_by_category', icon: Layers }
  ];

  const quickFilterButtons = [
    { id: 'all', nameKey: 'filter_all', icon: Layers },
    { id: 'En cours', nameKey: 'filter_in_progress', icon: Activity },
    { id: 'Publié', nameKey: 'filter_published', icon: CheckCircle },
    { id: 'Validé', nameKey: 'filter_validated', icon: Shield }
  ];

  const statCards = [
    { labelKey: 'stat_total', value: stats.total, icon: BarChart3, color: 'from-blue-500 to-blue-600' },
    { labelKey: 'stat_in_progress', value: stats.enCours, icon: Activity, color: 'from-amber-500 to-amber-600' },
    { labelKey: 'stat_published', value: stats.publies, icon: CheckCircle, color: 'from-green-500 to-green-600' },
    { labelKey: 'stat_monthly', value: stats.mensuels, icon: Calendar, color: 'from-cyan-500 to-cyan-600' },
    { labelKey: 'stat_pending_validations', value: stats.validationsAttente, icon: Clock, color: 'from-red-500 to-red-600' }
  ];

  const rapportDistribution = {
    all_rapports: {
      titleKey: 'dist_overview',
      data: [
        { nameKey: 'dist_in_progress', filterKey: 'En cours', count: stats.enCours, color: 'bg-gradient-to-r from-amber-500 to-amber-600' },
        { nameKey: 'dist_published', filterKey: 'Publié', count: stats.publies, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { nameKey: 'dist_monthly', filterKey: 'Mensuel', count: stats.mensuels, color: 'bg-gradient-to-r from-cyan-500 to-cyan-600' },
        { nameKey: 'dist_pending_validations', filterKey: '__pending__', count: stats.validationsAttente, color: 'bg-gradient-to-r from-red-500 to-red-600' }
      ]
    },
    par_type: {
      titleKey: 'dist_by_type',
      data: [
        { nameKey: 'dist_type_monthly', filterKey: 'Mensuel', count: rapports.filter(r => r.type_rapport === 'Mensuel').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { nameKey: 'dist_type_quarterly', filterKey: 'Trimestriel', count: rapports.filter(r => r.type_rapport === 'Trimestriel').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { nameKey: 'dist_type_semiannual', filterKey: 'Semestriel', count: rapports.filter(r => r.type_rapport === 'Semestriel').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { nameKey: 'dist_type_annual', filterKey: 'Annuel', count: rapports.filter(r => r.type_rapport === 'Annuel').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' }
      ]
    },
    par_statut: {
      titleKey: 'dist_by_status',
      data: [
        { nameKey: 'dist_status_in_progress', filterKey: 'En cours', count: stats.enCours, color: 'bg-gradient-to-r from-amber-500 to-amber-600' },
        { nameKey: 'dist_status_finalized', filterKey: 'Finalisé', count: rapports.filter(r => r.statut_rapport === 'Finalisé').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { nameKey: 'dist_status_validated', filterKey: 'Validé', count: rapports.filter(r => r.statut_rapport === 'Validé').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { nameKey: 'dist_status_published', filterKey: 'Publié', count: stats.publies, color: 'bg-gradient-to-r from-green-500 to-green-600' }
      ]
    },
    par_categorie: {
      titleKey: 'dist_by_category',
      data: [
        { nameKey: 'dist_cat_financial', filterKey: 'Financier', count: rapports.filter(r => r.categorie_rapport === 'Financier').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { nameKey: 'dist_cat_accounting', filterKey: 'Comptable', count: rapports.filter(r => r.categorie_rapport === 'Comptable').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { nameKey: 'dist_cat_management', filterKey: 'Gestion', count: rapports.filter(r => r.categorie_rapport === 'Gestion').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { nameKey: 'dist_cat_fiscal', filterKey: 'Fiscal', count: rapports.filter(r => r.categorie_rapport === 'Fiscal').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' }
      ]
    }
  };

  const distributionInfo = rapportDistribution[mainViewMode] || rapportDistribution.all_rapports;

  const filteredRapports = useMemo(() => {
    const base = rapports.filter(r => {
      if (statusFilter !== 'all' && r.statut_rapport !== statusFilter) return false;

      if (searchTerm && !r.rapport_ref?.toLowerCase().includes(searchTerm.toLowerCase()) && !r.titre_rapport?.toLowerCase().includes(searchTerm.toLowerCase()) && !r.categorie_rapport?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      if (mainViewMode === 'all_rapports') {
        if (quickFilter !== 'all' && r.statut_rapport !== quickFilter) return false;
      }
      if (activeDistributionFilter) {
        if (activeDistributionFilter === '__pending__') {
          if (r.validation_daf && r.validation_dg) return false;
        } else {
          switch(mainViewMode) {
            case 'par_statut':
              if (activeDistributionFilter !== r.statut_rapport) return false;
              break;
            case 'par_type':
              if (activeDistributionFilter !== r.type_rapport) return false;
              break;
            case 'par_categorie':
              if (activeDistributionFilter !== r.categorie_rapport) return false;
              break;
            case 'all_rapports':
              if (activeDistributionFilter === 'En cours' && r.statut_rapport !== 'En cours') return false;
              if (activeDistributionFilter === 'Publié' && r.statut_rapport !== 'Publié') return false;
              if (activeDistributionFilter === 'Mensuel' && r.type_rapport !== 'Mensuel') return false;
              break;
          }
        }
      }
      return true;
    });
    return applyColumnFilters(base, columnFilters, columnFilterRules);
  }, [rapports, statusFilter, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, columnFilters, columnFilterRules]);

  const {
    selectedRecords: selectedRecords,
    setSelectedRecords: setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord: handleSelectRecordBase,
    clearSelection,
  } = useComptabiliteListRowSelection(filteredRapports, (row) => row.id);
  const handleSelectRecord = handleSelectRecordBase;
  
  

  const {
    qawaemColumnDefs,
    handleToggleColumn,
    qawaemVisibleColumnCount,
    isColVisible,
    visibleDataColumnCount } = useComptabiliteTableColumnVisibility(RAPPORTS_LIST_COLUMNS, p);
  const rapportsTableColSpan = visibleDataColumnCount + 2;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (editingRecord) {
        await updateRapportPeriodique(editingRecord.id, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'التقرير الدوري' }));
      } else {
        await createRapportPeriodique(submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'التقرير الدوري' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message }));
    } finally {
      resetForm();
      loadRapports();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ ...record });
    setRapportsperiodiquesFormTab('rapportInfo');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('confirm_delete'))) {
      try {
        await deleteRapportPeriodique(id);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'التقرير الدوري' }));
        loadRapports();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('confirm_bulk_delete', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => deleteRapportPeriodique(id)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'التقارير الدورية' }));
        setSelectedRecords([]);
        clearSelection();
        loadRapports();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error', { message: error.message }));
      }
    }
  };

  const emptyFormData = () => ({
    rapport_ref: '',
    titre_rapport: '',
    description_rapport: '',
    type_rapport: '',
    categorie_rapport: '',
    exercice_fiscal: fiscalYearLabel || resolveFiscalYearLabel(),
    periode_debut: '',
    periode_fin: '',
    statut_rapport: '',
    niveau_confidentialite: '',
    format_rapport: '',
    validation_daf: '',
    archivage_obligatoire: '',
    conformite_reglementaire: '',
    notes: '',
  });

  const resetForm = () => {
    setFormData(emptyFormData());
    setEditingRecord(null);
    setShowModal(false);
    setRapportsperiodiquesFormTab('rapportInfo');
  };

  const openCreateModal = () => {
    setEditingRecord(null);
    setFormData(emptyFormData());
    setRapportsperiodiquesFormTab('rapportInfo');
    setShowModal(true);
  };

  const statutOptions = useMemo(() => [
    { value: 'En cours', label: p('status_in_progress'), icon: Activity, style: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    { value: 'Finalisé', label: p('status_finalized'), icon: CheckCircle, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'Validé', label: p('status_validated'), icon: Shield, style: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    { value: 'Publié', label: p('status_published'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'Archivé', label: p('status_archived'), icon: XCircle, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' }
  ], [t]);

  const statusFilterLabel = statusFilter === 'all' ? p('status') : translateStatut(statusFilter);

  const typeSelectOptions = useMemo(() => [
    { value: 'Mensuel', label: p('type_monthly') },
    { value: 'Trimestriel', label: p('type_quarterly') },
    { value: 'Semestriel', label: p('type_semiannual') },
    { value: 'Annuel', label: p('type_annual') },
    { value: 'Exceptionnel', label: p('type_exceptional') }
  ], [t]);

  const categorySelectOptions = useMemo(() => [
    { value: 'Financier', label: p('cat_financial') },
    { value: 'Comptable', label: p('cat_accounting') },
    { value: 'Gestion', label: p('cat_management') },
    { value: 'Fiscal', label: p('cat_fiscal') },
    { value: 'Réglementaire', label: p('cat_regulatory') },
    { value: 'Interne', label: p('cat_internal') }
  ], [t]);

  const formatSelectOptions = useMemo(() => [
    { value: 'PDF', label: p('format_pdf') },
    { value: 'Excel', label: p('format_excel') },
    { value: 'Word', label: p('format_word') },
    { value: 'HTML', label: p('format_html') },
    { value: 'PowerPoint', label: p('format_powerpoint') }
  ], [t]);

  const confidentialitySelectOptions = useMemo(() => [
    { value: 'Public', label: p('conf_public') },
    { value: 'Interne', label: p('conf_internal') },
    { value: 'Confidentiel', label: p('conf_confidential') },
    { value: 'Très Confidentiel', label: p('conf_highly_confidential') }
  ], [t]);

  const getStatutBadge = (statut) => {
    const config = statutOptions.find(s => s.value === statut) || statutOptions[0];
    const { label, icon: Icon, style} = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}><Icon size={14} />{label}</span>;
  };

  return (
    <div className={`space-y-3 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <ComptabiliteBulkDeleteFilterAction
              count={selectedRecords.length}
              onClick={handleBulkDelete}
              label={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
              title={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
            />
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : null}
            <FacturationViewModeToggle
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              isLight={isDashboardLightMode}
              cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
              tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
            />
          </div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <BarChart3 className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={loadRapports} className="refresh-button" title={p('refresh')}><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}>
            <Filter className="h-5 w-5" /><span className="hidden sm:inline">{p('advanced_options')}</span>{showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2" onClick={openCreateModal}>
            <Plus className="h-5 w-5" /><span>{p('add_button')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{p(stat.labelKey)}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(tab.nameKey)}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_rapports' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(filter.nameKey)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{p(distributionInfo.titleKey)}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                <button key={item.nameKey} onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.filterKey ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>{p(item.nameKey)}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {isColVisible('rapport_ref') ? (
                    <TableColumnHeaderFilter columnKey="rapport_ref" label={p('col_reference')} value={columnFilters.rapport_ref || ''} onChange={(v) => setColumnFilter('rapport_ref', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('titre_rapport') ? (
                    <TableColumnHeaderFilter columnKey="titre_rapport" label={p('col_report_title')} value={columnFilters.titre_rapport || ''} onChange={(v) => setColumnFilter('titre_rapport', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('type_rapport') ? (
                    <TableColumnHeaderFilter columnKey="type_rapport" label={p('col_type')} value={columnFilters.type_rapport || ''} onChange={(v) => setColumnFilter('type_rapport', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('categorie_rapport') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_category')}</th>
                  ) : null}
                  {isColVisible('date_generation') ? (
                    <TableColumnHeaderFilter columnKey="date_generation" label={p('col_generation_date')} value={columnFilters.date_generation || ''} onChange={(v) => setColumnFilter('date_generation', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('statut_rapport') ? (
                    <TableColumnHeaderFilter columnKey="statut_rapport" label={p('col_status')} value={columnFilters.statut_rapport || ''} onChange={(v) => setColumnFilter('statut_rapport', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                  thClassName={COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredRapports.length > 0 ? (
                  filteredRapports.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(record.id)}
                        onChange={() => handleSelectRecord(record.id)}
                      />
                      {isColVisible('rapport_ref') ? (
                      <td className="px-6 py-4"><span className="text-white font-semibold">{record.rapport_ref}</span></td>
                      ) : null}
                      {isColVisible('titre_rapport') ? (
                      <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-semibold">{record.titre_rapport}</span><span className="text-white/50 text-xs mt-0.5">{record.exercice_fiscal || p('na')}</span></div></td>
                      ) : null}
                      {isColVisible('type_rapport') ? (
                      <td className="px-6 py-4"><div className="flex flex-col"><span className="text-white font-semibold">{translateType(record.type_rapport)}</span><span className="text-white/50 text-xs mt-0.5">{record.format_rapport || p('format_pdf')}</span></div></td>
                      ) : null}
                      {isColVisible('categorie_rapport') ? (
                      <td className="px-6 py-4"><span className="text-white/70 text-sm">{translateCategory(record.categorie_rapport) || '-'}</span></td>
                      ) : null}
                      {isColVisible('date_generation') ? (
                      <td className="px-6 py-4"><div className="flex flex-col"><div className="flex items-center gap-1.5 text-white font-semibold"><Clock size={14} className="text-white/50" />{record.date_generation ? new Date(record.date_generation).toLocaleDateString(dateLocale) : p('na')}</div></div></td>
                      ) : null}
                      {isColVisible('statut_rapport') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          {getStatutBadge(record.statut_rapport)}
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                            onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                            onEdit={() => handleEdit(record)}
                            onDelete={() => handleDelete(record.id)}
                          />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={rapportsTableColSpan} className="text-center py-12 text-white/60"><BarChart3 size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_title')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredRapports.length > 0 ? (
              filteredRapports.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><BarChart3 size={20} className="text-emerald-400" />{record.titre_rapport}</h3>
                        <div className="flex">{getStatutBadge(record.statut_rapport)}</div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_report_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_ref')}:</span> {record.rapport_ref}</div>
                            <div><span className="font-medium">{p('card_type')}:</span> {translateType(record.type_rapport)}</div>
                            <div><span className="font-medium">{p('card_category')}:</span> {translateCategory(record.categorie_rapport)}</div>
                            <div><span className="font-medium">{p('card_exercise')}:</span> {record.exercice_fiscal}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_dates')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_generation')}:</span> {record.date_generation ? new Date(record.date_generation).toLocaleDateString(dateLocale) : p('na')}</div>
                            <div><span className="font-medium">{p('card_publication')}:</span> {record.date_publication ? new Date(record.date_publication).toLocaleDateString(dateLocale) : p('na')}</div>
                            <div><span className="font-medium">{p('card_start')}:</span> {record.periode_debut || p('na')}</div>
                            <div><span className="font-medium">{p('card_end')}:</span> {record.periode_fin || p('na')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_validation')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_daf')}:</span> {record.validation_daf ? p('validated') : p('pending')}</div>
                            <div><span className="font-medium">{p('card_dg')}:</span> {record.validation_dg ? p('validated') : p('pending')}</div>
                            <div><span className="font-medium">{p('card_format')}:</span> {record.format_rapport || p('format_pdf')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_compliance')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_level')}:</span> {record.niveau_confidentialite || p('internal')}</div>
                            <div><span className="font-medium">{p('card_archiving')}:</span> {record.archivage_obligatoire ? p('mandatory') : p('no')}</div>
                            <div><span className="font-medium">{p('card_compliant')}:</span> {record.conformite_reglementaire ? p('yes') : p('no')}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><BarChart3 size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty_title')}</p></div>
            )}
          </div>
        )}
      </div>

      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
          onClick={() => setShowViewModal(false)}
          role="presentation"
        >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_modal_title')}</h2>
                <button onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><BarChart3 size={18} className="text-sky-400" />{p('section_report_info')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className="text-white/60 text-sm mb-1">{p('label_reference')}</p><p className="text-white font-medium">{viewingRecord.rapport_ref}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_title')}</p><p className="text-white font-medium">{viewingRecord.titre_rapport}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_type')}</p><p className="text-white font-medium">{translateType(viewingRecord.type_rapport)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_category')}</p><p className="text-white font-medium">{translateCategory(viewingRecord.categorie_rapport)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_fiscal_year')}</p><p className="text-white font-medium">{viewingRecord.exercice_fiscal}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_format')}</p><p className="text-white font-medium">{viewingRecord.format_rapport}</p></div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><FileText size={18} className="text-amber-400" />{p('section_content')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-white/60 text-sm mb-1">{p('label_generation_date')}</p><p className="text-white font-medium">{viewingRecord.date_generation ? new Date(viewingRecord.date_generation).toLocaleDateString(dateLocale) : p('na')}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_publication_date')}</p><p className="text-white font-medium">{viewingRecord.date_publication ? new Date(viewingRecord.date_publication).toLocaleDateString(dateLocale) : p('na')}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_sections_count')}</p><p className="text-white font-medium">{viewingRecord.nombre_sections || 0}</p></div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><CheckCircle size={18} className="text-emerald-400" />{p('section_validation')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-white/60 text-sm mb-1">{p('label_daf_validation')}</p><p className="text-white font-medium">{viewingRecord.validation_daf ? p('validated') : p('pending')}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_dg_validation')}</p><p className="text-white font-medium">{viewingRecord.validation_dg ? p('validated') : p('pending')}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_recipients')}</p><p className="text-white font-medium">{viewingRecord.destinataires_nombre || 0}</p></div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Shield size={18} className="text-violet-400" />{p('section_compliance')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-white/60 text-sm mb-1">{p('label_confidentiality')}</p><p className="text-white font-medium">{viewingRecord.niveau_confidentialite || p('internal')}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_mandatory_archiving')}</p><p className="text-white font-medium">{viewingRecord.archivage_obligatoire ? p('yes') : p('no')}</p></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_regulatory_compliance')}</p><p className="text-white font-medium">{viewingRecord.conformite_reglementaire ? p('compliant') : p('non_compliant')}</p></div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
        </div>,
        document.body
      )}

      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`} style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>{editingRecord ? p('modal_edit') : p('modal_new')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="rapport-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><CheckCircle size={18} />{editingRecord ? p('btn_update') : p('btn_create')}</button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}><X size={24} /></button>
              </div>
            </div>
            <div className={`shrink-0 px-6 pt-3 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <div className="flex flex-wrap gap-1" role="tablist">
                {rapportsperiodiquesFormTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = rapportsperiodiquesFormTab === tab.id;
                  return (
                    <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setRapportsperiodiquesFormTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? (isDashboardLightMode ? 'bg-[#6e9b6e]/15 text-forest-900 border-b-2 border-[#6e9b6e] -mb-px' : 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px') : (isDashboardLightMode ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80' : 'text-white/55 hover:text-white hover:bg-white/5')}`}>
                      <TabIcon size={16} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="rapport-form" onSubmit={handleSubmit}>
                {rapportsperiodiquesFormTab === 'rapportInfo' && (
                  <div className="space-y-6">
                  <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_report_title')}</label><input type="text" required value={formData.titre_rapport} onChange={(e) => setFormData({ ...formData, titre_rapport: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_report_title')} /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_report_type')}</label><CustomSelect value={formData.type_rapport} onChange={(value) => setFormData({ ...formData, type_rapport: value })} options={typeSelectOptions} searchable={false} /></div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_category')}</label><CustomSelect value={formData.categorie_rapport} onChange={(value) => setFormData({ ...formData, categorie_rapport: value })} options={categorySelectOptions} searchable={false} /></div>
                            <ComptabiliteFiscalYearField label={p('form_fiscal_year')} value={formData.exercice_fiscal} onChange={(val) => setFormData({ ...formData, exercice_fiscal: val })} placeholder={p('placeholder_fiscal_year')} required />
                          </div>
                          <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_description')}</label><textarea value={formData.description_rapport} onChange={(e) => setFormData({ ...formData, description_rapport: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_description')} /></div>
                  </div>
                    <div className="pt-6 border-t border-white/10">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_format')}</label><CustomSelect value={formData.format_rapport} onChange={(value) => setFormData({ ...formData, format_rapport: value })} options={formatSelectOptions} searchable={false} /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_status')}</label><CustomSelect value={formData.statut_rapport} onChange={(value) => setFormData({ ...formData, statut_rapport: value })} options={statutOptions.map((s) => ({ value: s.value, label: s.label }))} searchable={false} /></div>
                      </div>
                    </div>
                  </div>
                )}
                {rapportsperiodiquesFormTab === 'validationConformite' && (
<div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_period_start')}</label><input type="date" value={formData.periode_debut} onChange={(e) => setFormData({ ...formData, periode_debut: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_period_end')}</label><input type="date" value={formData.periode_fin} onChange={(e) => setFormData({ ...formData, periode_fin: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" /></div>
                          </div>
                    <div className="pt-6 border-t border-white/10">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_confidentiality')}</label><CustomSelect value={formData.niveau_confidentialite} onChange={(value) => setFormData({ ...formData, niveau_confidentialite: value })} options={confidentialitySelectOptions} searchable={false} /></div>
                            <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_notes')}</label><input type="text" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_notes')} /></div>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default RapportsperiodiquesPage;


